import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAction, useData } from "@/hooks/useData";
import { Button, DefinitionList, Field, Input, SectionCard, Select } from "@/components/ui";
import { aed, currentMonth } from "@/lib/format";

const EMPTY = { basic: 0, housing: 0, transport: 0, communication: 0, other_allowance: 0, bonus: 0, unpaid_leave_days: 0, other_deduction: 0, loan: 0, working_days: 22 };

const EARNINGS = [
  ["basic", "Basic"], ["housing", "Housing"], ["transport", "Transport"],
  ["communication", "Communication"], ["other_allowance", "Other allowance"], ["bonus", "Bonus"],
];

export default function SalarySlipGenerator() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { run, pending } = useAction();
  const { data: employees } = useData(() => api.employees.list(user), [user]);
  const [empId, setEmpId] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [comp, setComp] = useState(EMPTY);

  const choose = (id) => {
    setEmpId(id);
    const c = employees.find((e) => e.id === id)?.salary_components;
    if (c) setComp({ ...EMPTY, basic: c.basic, housing: c.housing, transport: c.transport, communication: c.communication, other_allowance: c.other });
  };

  const num = (k) => ({
    type: "number", min: 0, step: k === "unpaid_leave_days" ? 0.5 : "any", value: comp[k],
    onChange: (e) => setComp((c) => ({ ...c, [k]: e.target.value === "" ? 0 : Number(e.target.value) })),
  });

  const calc = api.salarySlips.preview(comp);

  const generate = async () => {
    const { ok } = await run(() => api.salarySlips.create({ employee_id: empId, month, ...comp }), "Salary slip generated");
    if (ok) nav("/payroll/slips");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <SectionCard title="Employee & Period" subtitle="Components prefill from the employee's contract">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Employee">
              <Select
                value={empId}
                onChange={(e) => choose(e.target.value)}
                placeholder={employees ? "Select employee" : "Loading…"}
                options={(employees || []).map((e) => ({ value: e.id, label: `${e.name} (${e.id})` }))}
              />
            </Field>
            <Field label="Payroll month"><Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} /></Field>
            <Field label="Working days"><Input {...num("working_days")} min={1} /></Field>
            <Field label="Unpaid leave days"><Input {...num("unpaid_leave_days")} /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Earnings" subtitle="AED">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {EARNINGS.map(([k, label]) => <Field key={k} label={label}><Input {...num(k)} className="tabular-nums" /></Field>)}
          </div>
        </SectionCard>

        <SectionCard title="Deductions" subtitle="AED">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Loan / advance"><Input {...num("loan")} className="tabular-nums" /></Field>
            <Field label="Other"><Input {...num("other_deduction")} className="tabular-nums" /></Field>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Calculation" className="self-start lg:sticky lg:top-4">
        <DefinitionList rows={[
          { label: "Gross salary", value: aed(calc.gross) },
          { label: "Per-day rate", value: aed(calc.per_day) },
          { label: "Loss of pay", value: <span className="text-rose-600">− {aed(calc.lop_deduction)}</span> },
          { label: "Loan", value: <span className="text-rose-600">− {aed(comp.loan)}</span> },
          { label: "Other deductions", value: <span className="text-rose-600">− {aed(comp.other_deduction)}</span> },
        ]} />
        <div className="neu-inset mt-4 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Net pay</p>
          <p className="text-2xl font-extrabold text-brand-orange tabular-nums mt-1">AED {aed(calc.net_pay)}</p>
        </div>
        <div className="flex gap-2 mt-4">
          <Button className="flex-1" onClick={() => nav("/payroll/slips")}>Cancel</Button>
          <Button variant="primary" className="flex-1" icon={<Save size={13} />} loading={pending} disabled={!empId} onClick={generate}>Generate</Button>
        </div>
      </SectionCard>
    </div>
  );
}
